"use client";

/**
 * P2-06h · Recently-viewed client island.
 *
 * Renders the SSR-hydrated cards immediately (no flash), then tracks the P2-08
 * hook: if the visitor's list diverges from what the server sent (a view
 * recorded without a reload), it refetches and swaps in the new cards — keeping
 * the current cards on screen while the fetch is in flight or has failed (a
 * skeleton shows only when there is nothing to keep). Applies `excludeCurrent`
 * + `limit`, and renders nothing when the list is empty — so a visitor with no
 * history never sees a heading or box.
 */
import { keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";

import type { ProductCardView } from "~/components/storefront/product-card";
import { useTranslator } from "~/i18n/provider";
import { useRecentlyViewed } from "~/lib/recently-viewed";
import type { RecentlyViewedConfig } from "~/server/content/blocks/recently-viewed";
import { api } from "~/trpc/react";
import {
  ProductsBlockLayoutView,
  ProductsBlockSkeletonView,
} from "./products-block-view";

function idsEqual(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

export function RecentlyViewedClient({
  config,
  initialIds,
  initialProducts,
  currentProductId,
}: {
  config: RecentlyViewedConfig;
  initialIds: string[];
  initialProducts: ProductCardView[];
  /** Set when the block is reused on a PDP (P3); undefined on the homepage. */
  currentProductId?: string;
}) {
  const t = useTranslator();
  // The hook is empty on the very first client render, then mirrors the cookie.
  const liveIds = useRecentlyViewed();
  // Compare/fetch only the window the block can render — the server sliced
  // `initialIds` the same way, so a longer cookie list alone isn't divergence.
  const relevantCount = config.limit + (config.excludeCurrent ? 1 : 0);
  const live = liveIds.slice(0, relevantCount);
  const ids = live.length > 0 ? live : initialIds;
  const sameAsInitial = idsEqual(ids, initialIds);

  // Only re-fetch when the live list has actually diverged from the SSR list.
  const query = api.content.getProductsByIds.useQuery(
    { ids },
    {
      enabled: !sameAsInitial && ids.length > 0,
      placeholderData: keepPreviousData,
    },
  );

  // Fresh data when we have it; while a divergence fetch is in flight (or has
  // failed) keep showing the cards we already have — never a stuck skeleton.
  const source: ProductCardView[] | null = sameAsInitial
    ? initialProducts
    : (query.data ??
      (initialProducts.length > 0 || query.isError ? initialProducts : null));

  const products = useMemo(() => {
    if (!source) return null;
    return source
      .filter((p) => !(config.excludeCurrent && p.id === currentProductId))
      .slice(0, config.limit);
  }, [source, config.excludeCurrent, config.limit, currentProductId]);

  if (products === null) {
    // Diverged with nothing on screen to keep (first PDP view) — hold space
    // with a skeleton while the fetch is in flight.
    return ids.length > 0 ? (
      <ProductsBlockSkeletonView
        layout={config.layout}
        count={Math.min(config.limit, 4)}
        loadingLabel={t("blocks.loadingProducts")}
      />
    ) : null;
  }
  if (products.length === 0) return null;

  return (
    <ProductsBlockLayoutView
      title={config.title}
      layout={config.layout}
      products={products}
    />
  );
}
